/*
  Inner Botanica – The Letter (partner-letter): application logic.
  Content/lexicons live in partner-letter-data.js (window.IB_PL_DATA).
  Settings/asset URLs come from the section (window.IB_PL_CONFIG).

  Privacy contract: answers live in memory + sessionStorage only. They are
  never transmitted. Analytics carry event names + step ids only — never
  answer content. Her free text renders via textContent only (never innerHTML)
  and is never paraphrased. The letter is composed entirely on the device from
  brand-approved copy blocks — there is no server and no AI in the loop.
*/
(function () {
  "use strict";

  var DATA = window.IB_PL_DATA;
  var CONFIG = window.IB_PL_CONFIG || {};
  var S = DATA.strings;
  var L = DATA.letter;
  var STORE_KEY = "ib_pl_state_v1";

  /* ------------------------------------------------------------ lookups */
  function indexBy(list) {
    var m = {};
    list.forEach(function (x) { m[x.id] = x; });
    return m;
  }
  var LEX = {
    phys: DATA.phys, emo: DATA.emo, trig: DATA.trig,
    not: DATA.notReasons, help: DATA.help, load: DATA.load, core: DATA.core
  };
  var IDX = {};
  Object.keys(LEX).forEach(function (k) { IDX[k] = indexBy(LEX[k]); });
  var TONE_IDX = indexBy(DATA.tones);
  var SCREEN_DEFS = {};
  DATA.screens.forEach(function (d) { SCREEN_DEFS[d.id] = d; });

  /* ---- keepsake motifs (faint watercolor watermark behind the letter) ---- */
  var MOTIFS = window.IB_PL_MOTIFS || [{ id: "none", label: "None", svg: "" }];
  var MOTIF_IDX = indexBy(MOTIFS);
  var DEFAULT_MOTIF = MOTIF_IDX[CONFIG.defaultMotif] ? CONFIG.defaultMotif : "eucalyptus";
  if (!MOTIF_IDX[DEFAULT_MOTIF]) DEFAULT_MOTIF = "none";
  function motifDataUri(svg) {
    return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
  }

  /* short forms for the "it isn't really about ___" line (heart block) */
  var TRIG_SHORT = {
    chewing: "the sound", breathing: "the breathing", joke_crit: "the joke",
    fixing: "the advice", interrupted: "being interrupted", whats_wrong: "the question",
    noise: "the noise", mess: "the mess", rushed: "the rush",
    touched: "the touch", unseen: "any one thing", questions: "the questions",
    plans: "the plan"
  };

  /* -------------------------------------------------------------- state */
  function defaultNot() {
    return DATA.notReasons.filter(function (r) { return r.on; }).map(function (r) { return r.id; });
  }
  function freshAnswers() {
    return {
      v: 1,
      from: "", to: "",
      phys: [], emo: [], trig: [],
      not: defaultNot(),
      help: [], load: [], core: [],
      tone: "tender",
      motif: DEFAULT_MOTIF,
      ps: { t: "", none: false }
    };
  }
  var state = {
    answers: freshAnswers(),
    screen: "intro",
    history: [],
    composed: "",
    edited: false,
    fromShare: false
  };

  /* ------------------------------------------------------------ persist */
  var memStore = null;
  function save() {
    var payload = JSON.stringify({
      answers: state.answers,
      screen: state.screen,
      history: state.history,
      composed: state.edited ? state.composed : "",
      edited: state.edited
    });
    try { sessionStorage.setItem(STORE_KEY, payload); }
    catch (e) { memStore = payload; }
  }
  function load() {
    var raw = null;
    try { raw = sessionStorage.getItem(STORE_KEY); } catch (e) { raw = memStore; }
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (parsed && parsed.answers && parsed.answers.v === 1) return parsed;
    } catch (e) { /* corrupt — ignore */ }
    return null;
  }
  function clearSaved() {
    try { sessionStorage.removeItem(STORE_KEY); } catch (e) { /* noop */ }
    memStore = null;
  }

  /* ---------------------------------------------------------- analytics
     Step ids only. Never answer content. */
  function track(event, stepId) {
    try {
      var payload = { event: event };
      if (stepId) payload.step_id = stepId;
      (window.dataLayer = window.dataLayer || []).push(payload);
      document.dispatchEvent(new CustomEvent("funnelocity:letter-event", {
        detail: { event: event, step_id: stepId || null }
      }));
    } catch (e) { /* analytics must never break the flow */ }
  }

  /* --------------------------------------------------------- dom helpers */
  function byId(id) { return document.getElementById(id); }
  function h(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }
  function btn(cls, label, onClick) {
    var b = h("button", cls, label);
    b.type = "button";
    b.addEventListener("click", onClick);
    return b;
  }

  /* ---------------------------------------------------------------- path */
  function buildPath() { return DATA.screens.map(function (s) { return s.id; }); }
  function nextKey(afterKey) {
    var p = buildPath();
    var i = p.indexOf(afterKey);
    return i === -1 || i + 1 >= p.length ? null : p[i + 1];
  }

  /* --------------------------------------------------------- view states */
  var VIEW_IDS = ["ib-pl-intro", "ib-pl-interview", "ib-pl-transition", "ib-pl-reveal", "ib-pl-after"];
  function showView(id) {
    VIEW_IDS.forEach(function (v) {
      var node = byId(v);
      if (node) node.classList.toggle("is-active", v === id);
    });
    var progress = byId("ib-pl-progress");
    if (progress) progress.hidden = id !== "ib-pl-interview";
    window.scrollTo(0, 0);
  }

  /* --------------------------------------------------------------- router */
  function go(key, opts) {
    opts = opts || {};
    var def = SCREEN_DEFS[key];
    if (!def) return;
    if (!opts.silent) {
      if (state.screen !== key) state.history.push(state.screen);
      state.screen = key;
    }
    showView("ib-pl-interview");
    renderScreen(def);
    updateProgress(key);
    track("pl_step", key);
    save();
  }
  function goBack() {
    var prev = state.history.pop();
    if (!prev || prev === "intro") {
      state.screen = "intro";
      showView("ib-pl-intro");
      save();
      return;
    }
    state.screen = prev;
    showView("ib-pl-interview");
    renderScreen(SCREEN_DEFS[prev]);
    updateProgress(prev);
    save();
  }
  function advance(fromKey) {
    var nk = nextKey(fromKey);
    if (nk) { go(nk); return; }
    reveal();
  }
  function updateProgress(key) {
    var p = buildPath();
    var i = p.indexOf(key);
    var fill = byId("ib-pl-progress-fill");
    if (fill && i !== -1) fill.style.width = Math.round(((i + 1) / (p.length + 1)) * 100) + "%";
  }

  /* ------------------------------------------------------------ renderers */
  function screenShell(def) {
    var mount = byId("ib-pl-screen");
    mount.textContent = "";
    var card = h("div", "ib-pl-card");
    var head = h("h2", "ib-pl-card__prompt");
    head.tabIndex = -1;
    head.textContent = def.prompt;
    card.appendChild(head);
    if (def.sub) card.appendChild(h("p", "ib-pl-card__sub", def.sub));
    mount.appendChild(card);
    window.requestAnimationFrame(function () { head.focus(); });
    return card;
  }
  function footerNav(card, nextEnabled, onNext, nextLabel) {
    var foot = h("div", "ib-pl-card__footer");
    foot.appendChild(btn("ib-pl-btn ib-pl-btn--ghost", S.back, goBack));
    var next = btn("ib-pl-btn ib-pl-btn--primary", nextLabel || S.next, onNext);
    next.disabled = !nextEnabled;
    foot.appendChild(next);
    card.appendChild(foot);
    return next;
  }
  function setPressed(node, on) {
    node.setAttribute("aria-pressed", on ? "true" : "false");
    node.classList.toggle("is-on", !!on);
  }
  function chipButton(label, pressed, onToggle) {
    var c = btn("ib-pl-chip", label, function () { onToggle(c); });
    c.setAttribute("aria-pressed", pressed ? "true" : "false");
    if (pressed) c.classList.add("is-on");
    return c;
  }

  /* identity (names) */
  function renderIdentity(def) {
    var a = state.answers;
    var card = screenShell(def);
    var wrap = h("div", "ib-pl-identity");
    function field(labelText, value, placeholder, onInput) {
      var block = h("label", "ib-pl-field");
      block.appendChild(h("span", "ib-pl-field__label", labelText));
      var input = h("input", "ib-pl-input");
      input.type = "text";
      input.maxLength = 60;
      input.value = value || "";
      input.placeholder = placeholder;
      input.addEventListener("input", function () { onInput(input.value); save(); });
      block.appendChild(input);
      return block;
    }
    wrap.appendChild(field(S.fromLabel, a.from, S.fromPlaceholder, function (v) { a.from = v; }));
    wrap.appendChild(field(S.toLabel, a.to, S.toPlaceholder, function (v) { a.to = v; }));
    card.appendChild(wrap);
    footerNav(card, true, function () { advance(def.id); });
  }

  /* chips (phys, emo, trig, not, help, load, core) */
  function renderChips(def) {
    var a = state.answers;
    var selected = a[def.key];
    var card = screenShell(def);
    var wrap = h("div", "ib-pl-chips");
    var nextBtn;
    var maxNote = null;

    function refreshNext() {
      if (nextBtn) nextBtn.disabled = selected.length < (def.min || 0);
      if (maxNote) maxNote.hidden = !(def.max && selected.length >= def.max);
    }
    function toggle(id, node) {
      var i = selected.indexOf(id);
      if (i === -1) {
        if (def.max && selected.length >= def.max) return; /* cap reached */
        selected.push(id);
      } else {
        selected.splice(i, 1);
      }
      setPressed(node, selected.indexOf(id) !== -1);
      save();
      refreshNext();
    }
    def.chips.forEach(function (item) {
      wrap.appendChild(chipButton(item.label, selected.indexOf(item.id) !== -1, function (node) {
        toggle(item.id, node);
      }));
    });
    card.appendChild(wrap);

    if (def.max) {
      maxNote = h("p", "ib-pl-card__hint", "That's " + def.max + " — the three that matter most. Tap one off to swap.");
      maxNote.hidden = selected.length < def.max;
      card.appendChild(maxNote);
    }

    nextBtn = footerNav(card, selected.length >= (def.min || 0), function () { advance(def.id); });
    refreshNext();
  }

  /* single (tone) */
  function renderSingle(def) {
    var a = state.answers;
    var card = screenShell(def);
    var list = h("div", "ib-pl-options");
    def.options.forEach(function (opt) {
      var node = btn("ib-pl-chip ib-pl-chip--block", "", function () {
        a.tone = opt.id;
        Array.prototype.forEach.call(list.children, function (c) { setPressed(c, false); });
        setPressed(node, true);
        save();
        window.setTimeout(function () { advance(def.id); }, 240);
      });
      node.appendChild(h("span", "ib-pl-chip__label", opt.label));
      if (opt.hint) node.appendChild(h("span", "ib-pl-chip__hint", opt.hint));
      setPressed(node, a.tone === opt.id);
      list.appendChild(node);
    });
    card.appendChild(list);
    var foot = h("div", "ib-pl-card__footer");
    foot.appendChild(btn("ib-pl-btn ib-pl-btn--ghost", S.back, goBack));
    card.appendChild(foot);
  }

  /* text (ps) */
  function renderText(def) {
    var a = state.answers;
    var card = screenShell(def);
    var ta = h("textarea", "ib-pl-textarea");
    ta.maxLength = def.maxlen;
    ta.rows = 4;
    ta.placeholder = def.placeholder || "";
    ta.setAttribute("aria-label", def.prompt);
    ta.value = a.ps.t || "";
    ta.addEventListener("input", function () { a.ps.t = ta.value; a.ps.none = false; save(); });
    card.appendChild(ta);
    var skip = btn("ib-pl-btn ib-pl-btn--ghost ib-pl-skip", def.skipLabel, function () {
      a.ps.t = ""; a.ps.none = true; save();
      advance(def.id);
    });
    card.appendChild(skip);
    footerNav(card, true, function () {
      if ((a.ps.t || "").trim()) a.ps.none = false;
      advance(def.id);
    }, "Write my letter");
  }

  function renderScreen(def) {
    switch (def.type) {
      case "identity": return renderIdentity(def);
      case "chips": return renderChips(def);
      case "single": return renderSingle(def);
      case "text": return renderText(def);
    }
  }

  /* ==================================================== composition engine
     Pure: answers -> letter plain text. Brand-approved blocks only; her free
     text passes through verbatim, quoted. */
  function fragList(key, ids) {
    var idx = IDX[key];
    return (ids || []).map(function (id) { return idx[id] ? idx[id].letter : ""; }).filter(Boolean);
  }
  function joinAnd(list) {
    if (!list.length) return "";
    if (list.length === 1) return list[0];
    if (list.length === 2) return list[0] + " and " + list[1];
    /* If any fragment carries its own comma (or em-dash aside), separate the
       list with semicolons so the boundaries stay legible. */
    var sep = list.some(function (f) { return f.indexOf(",") !== -1 || f.indexOf(" — ") !== -1; }) ? "; " : ", ";
    return list.slice(0, -1).join(sep) + sep + "and " + list[list.length - 1];
  }
  function joinSentences(list) {
    /* full first-person clauses -> "A. B. C" (caller adds the trailing period) */
    return list.join(". ");
  }
  function repTrigger(ids) {
    for (var i = 0; i < ids.length; i++) {
      if (TRIG_SHORT[ids[i]]) return TRIG_SHORT[ids[i]];
    }
    return L.trigMidFallback;
  }

  function composeLetter(a) {
    var tone = TONE_IDX[a.tone] ? a.tone : "tender";
    var paras = [];

    paras.push(L.salutation((a.to || "").trim()));
    paras.push(L.opening[tone] || L.opening.tender);

    var phys = fragList("phys", a.phys);
    if (phys.length) paras.push(L.physLead + joinAnd(phys) + L.physTail);

    var emo = fragList("emo", a.emo);
    if (emo.length) paras.push(L.emoLead + joinAnd(emo) + L.emoTail);

    var trig = fragList("trig", a.trig);
    if (trig.length) {
      paras.push(L.trigLead + joinAnd(trig) + ". " + L.trigMid + repTrigger(a.trig) + L.trigTail);
    }

    var not = fragList("not", a.not);
    if (not.length) paras.push(L.notLead + joinSentences(not) + L.notTail);

    var help = fragList("help", a.help);
    if (help.length) paras.push(L.helpLead + joinAnd(help) + L.helpTail);

    var load = fragList("load", a.load);
    if (load.length) paras.push(L.loadLead + joinAnd(load) + L.loadTail);

    var core = fragList("core", a.core);
    var coreText = core.length ? core.join("; ") : DATA.coreFallback;
    paras.push(L.coreLead + coreText + L.coreTail);

    if ((a.ps.t || "").trim()) paras.push(L.psLead + "“" + a.ps.t.trim() + "”");

    paras.push(L.closing[tone] || L.closing.tender);
    paras.push(L.signOff + "\n" + ((a.from || "").trim() || L.signFallback));

    return paras.join("\n\n");
  }

  /* ===================================================== letter DOM render */
  function paragraphsOf(text) {
    return text.split(/\n\n+/).map(function (p) { return p.replace(/\s+$/, ""); }).filter(function (p) { return p.length; });
  }
  function applyMotif(node, id) {
    var m = MOTIF_IDX[id];
    if (m && m.svg) {
      node.style.backgroundImage = "url(\"" + motifDataUri(m.svg) + "\")";
      node.hidden = false;
    } else {
      node.style.backgroundImage = "none";
      node.hidden = true;
    }
  }
  function updateWatermark() {
    var wm = document.querySelector("#ib-pl-letter .ib-pl-letter__watermark");
    if (wm) applyMotif(wm, state.answers.motif);
  }
  function renderMotifRow() {
    var row = byId("ib-pl-motif-row");
    if (!row) return;
    row.textContent = "";
    MOTIFS.forEach(function (m) {
      var sw = h("button", "ib-pl-motif__swatch");
      sw.type = "button";
      sw.setAttribute("aria-label", m.label);
      sw.title = m.label;
      if (m.id === "none") {
        sw.classList.add("ib-pl-motif__swatch--none");
        sw.appendChild(h("span", null, "None"));
      } else {
        var img = document.createElement("img");
        img.alt = m.label;
        img.src = motifDataUri(m.svg);
        sw.appendChild(img);
      }
      if (state.answers.motif === m.id) sw.classList.add("is-on");
      sw.addEventListener("click", function () {
        state.answers.motif = m.id;
        Array.prototype.forEach.call(row.children, function (c) { c.classList.remove("is-on"); });
        sw.classList.add("is-on");
        updateWatermark();
        if (pdf && pdf.warmMotif) pdf.warmMotif();
        save();
        track("pl_motif", m.id);
      });
      row.appendChild(sw);
    });
  }

  function renderLetterDOM() {
    var root = byId("ib-pl-letter");
    root.textContent = "";
    var wm = h("div", "ib-pl-letter__watermark");
    applyMotif(wm, state.answers.motif);
    root.appendChild(wm);
    var paras = paragraphsOf(state.composed);
    paras.forEach(function (p, i) {
      var cls = "ib-pl-letter__p";
      if (i === 0) cls += " ib-pl-letter__salutation";
      if (i === paras.length - 1) cls += " ib-pl-letter__sign";
      var node = h("p", cls);
      /* preserve intentional single newlines (signature) */
      p.split("\n").forEach(function (line, li) {
        if (li) node.appendChild(document.createElement("br"));
        node.appendChild(document.createTextNode(line));
      });
      root.appendChild(node);
    });
  }

  /* --------------------------------------------------------- edit toggle */
  var editing = false;
  function toggleEdit() {
    var letter = byId("ib-pl-letter");
    var editWrap = byId("ib-pl-edit");
    var editBtn = byId("ib-pl-edit-toggle");
    editing = !editing;
    if (editing) {
      track("pl_edit");
      var ta = byId("ib-pl-edit-area");
      ta.value = state.composed;
      letter.hidden = true;
      editWrap.hidden = false;
      editBtn.textContent = S.reveal.doneHint;
      editBtn.classList.add("is-editing");
      ta.focus();
    } else {
      var area = byId("ib-pl-edit-area");
      var val = (area.value || "").trim();
      if (val) { state.composed = val; state.edited = true; save(); }
      renderLetterDOM();
      letter.hidden = false;
      editWrap.hidden = true;
      editBtn.textContent = S.reveal.editHint;
      editBtn.classList.remove("is-editing");
    }
  }

  /* ================================================================ reveal */
  function reveal() {
    showView("ib-pl-transition");
    state.screen = "reveal";
    track("pl_compose");
    save();
    window.setTimeout(function () {
      if (!state.edited || !state.composed) state.composed = composeLetter(state.answers);
      renderLetterDOM();
      renderMotifRow();
      editing = false;
      var letter = byId("ib-pl-letter");
      var editWrap = byId("ib-pl-edit");
      var editBtn = byId("ib-pl-edit-toggle");
      if (letter) letter.hidden = false;
      if (editWrap) editWrap.hidden = true;
      if (editBtn) { editBtn.textContent = S.reveal.editHint; editBtn.classList.remove("is-editing"); }
      showView("ib-pl-reveal");
      track("pl_letter_view");
      pdf.prepare();
    }, prefersReducedMotion() ? 0 : 1100);
  }
  function prefersReducedMotion() {
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
    catch (e) { return false; }
  }

  /* ============================================================== PDF
     Multi-page typeset letter. Uses the DM Sans TTFs already shipped with the
     Doctor Kit; falls back to Helvetica if they don't load. */
  var pdf = (function () {
    var readyPromise = null;
    var fonts = { regular: null, bold: null, failed: false };

    function loadScript(src) {
      return new Promise(function (resolve, reject) {
        if (window.jspdf) { resolve(); return; }
        var sc = document.createElement("script");
        sc.src = src; sc.onload = resolve; sc.onerror = reject;
        document.head.appendChild(sc);
      });
    }
    function fetchB64(url) {
      return fetch(url).then(function (r) {
        if (!r.ok) throw new Error("font fetch failed");
        return r.arrayBuffer();
      }).then(function (buf) {
        var bytes = new Uint8Array(buf), bin = "", CHUNK = 0x8000;
        for (var i = 0; i < bytes.length; i += CHUNK) {
          bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
        }
        return btoa(bin);
      });
    }
    function ensureReady() {
      if (!readyPromise) {
        readyPromise = loadScript(CONFIG.assets.jspdf).then(function () {
          if (!CONFIG.assets.fontRegular || !CONFIG.assets.fontBold) { fonts.failed = true; return; }
          return Promise.all([fetchB64(CONFIG.assets.fontRegular), fetchB64(CONFIG.assets.fontBold)])
            .then(function (res) { fonts.regular = res[0]; fonts.bold = res[1]; })
            .catch(function () { fonts.failed = true; track("pl_pdf_font_fallback"); });
        });
      }
      return readyPromise;
    }
    function newDoc() {
      var doc = new window.jspdf.jsPDF({ unit: "pt", format: "letter" });
      if (!fonts.failed && fonts.regular && fonts.bold) {
        doc.addFileToVFS("DMSans-Regular.ttf", fonts.regular);
        doc.addFont("DMSans-Regular.ttf", "DMSans", "normal");
        doc.addFileToVFS("DMSans-Bold.ttf", fonts.bold);
        doc.addFont("DMSans-Bold.ttf", "DMSans", "bold");
      }
      return doc;
    }
    function fontName() { return fonts.failed || !fonts.regular ? "helvetica" : "DMSans"; }

    var PAGE = { w: 612, h: 792, margin: 60 };
    var INK = [27, 34, 37];
    var SLATE = [57, 91, 97];
    var BLUSH = [120, 43, 79];

    /* Rasterize the selected motif to a faint PNG (opacity baked in) so it can
       be stamped behind the letter text. Resolves null on any failure. */
    function rasterizeMotif(id) {
      return new Promise(function (resolve) {
        var m = MOTIF_IDX[id];
        if (!m || !m.svg) { resolve(null); return; }
        try {
          var sized = m.svg.replace("<svg ", "<svg width='600' height='600' ");
          var img = new Image();
          img.onload = function () {
            try {
              var c = document.createElement("canvas");
              c.width = 600; c.height = 600;
              var ctx = c.getContext("2d");
              ctx.globalAlpha = 0.12;
              ctx.drawImage(img, 0, 0, 600, 600);
              resolve(c.toDataURL("image/png"));
            } catch (e) { resolve(null); }
          };
          img.onerror = function () { resolve(null); };
          img.src = motifDataUri(sized);
        } catch (e) { resolve(null); }
      });
    }

    function render(doc, text, motifPng) {
      var f = fontName();
      var x = PAGE.margin;
      var w = PAGE.w - PAGE.margin * 2;
      var bottom = PAGE.h - PAGE.margin;
      var y = PAGE.margin;
      var size = 11.5, lh = size * 1.55;

      /* keepsake watermark, centered behind the text */
      if (motifPng) {
        try {
          var mSize = 300;
          doc.addImage(motifPng, "PNG", (PAGE.w - mSize) / 2, (PAGE.h - mSize) / 2, mSize, mSize);
        } catch (e) { /* skip watermark on any failure */ }
      }

      /* quiet header rule */
      doc.setDrawColor(BLUSH[0], BLUSH[1], BLUSH[2]);
      doc.setLineWidth(1);
      doc.line(x, y, x + 44, y);
      y += 26;

      var paras = paragraphsOf(text);
      paras.forEach(function (para, pi) {
        var isSalutation = pi === 0;
        var isSign = pi === paras.length - 1;
        var style = isSalutation ? "bold" : "normal";
        doc.setFont(f, style);
        doc.setFontSize(isSalutation ? 12.5 : size);
        doc.setTextColor(INK[0], INK[1], INK[2]);
        /* split on intentional newlines first (signature), then wrap */
        para.split("\n").forEach(function (rawLine) {
          var lines = doc.splitTextToSize(rawLine, w);
          lines.forEach(function (ln) {
            if (y > bottom) { doc.addPage(); y = PAGE.margin; }
            doc.text(ln, x, y);
            y += lh;
          });
        });
        y += isSign ? 0 : lh * 0.55; /* paragraph gap */
      });

      /* footer credit on the last page */
      doc.setFont(f, "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(SLATE[0], SLATE[1], SLATE[2]);
      doc.text(CONFIG.footerCredit || "Written with the Feminine Shift™ toolkit · Inner Botanica", x, PAGE.h - PAGE.margin + 24);
    }

    /* Pre-rasterize the chosen motif so the download click stays synchronous
       (a mid-click image decode would lose the user gesture and some browsers
       block the resulting download). */
    var motifCache = { id: " ", png: null };
    function warmMotif() {
      var id = state.answers.motif;
      if (motifCache.id === id) return Promise.resolve(motifCache.png);
      return rasterizeMotif(id).then(function (png) { motifCache = { id: id, png: png }; return png; });
    }
    function currentMotifPng() {
      return motifCache.id === state.answers.motif ? motifCache.png : null;
    }

    function prepare() {
      ensureReady().then(warmMotif).catch(function () { /* handled at download */ });
    }

    /* Deliver the PDF from its blob (jsPDF's own doc.save() bundles a file-saver
       that some desktop browsers/extensions block or throw on). Returns the mode
       so the UI can guide the user only when a real fallback happened. */
    function deliverPdf(doc, name) {
      var blob = doc.output("blob");
      var url = URL.createObjectURL(blob);
      window.setTimeout(function () { URL.revokeObjectURL(url); }, 60000);
      var env = isConstrainedBrowser();
      var a = document.createElement("a");
      if (("download" in a) && !env.iOS && !env.inApp) {
        a.href = url; a.download = name; a.rel = "noopener";
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        return "ok";
      }
      var w = window.open(url, "_blank");
      return w ? "newtab" : "blocked";
    }

    function isConstrainedBrowser() {
      var ua = navigator.userAgent || "";
      var iOS = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      var inApp = /Instagram|FBAN|FBAV|FB_IAB|Line\/|MicroMessenger/.test(ua);
      return { iOS: iOS, inApp: inApp };
    }

    function download() {
      var help = byId("ib-pl-download-help");
      return ensureReady().then(function () {
        if (!window.jspdf) throw new Error("jspdf unavailable");
        var doc = newDoc();
        render(doc, state.composed, currentMotifPng());
        var stamp = new Date().toISOString().slice(0, 10);
        var name = "A-Letter-" + stamp + ".pdf";
        var mode = deliverPdf(doc, name);
        if (help) {
          if (mode === "ok") { help.hidden = true; help.textContent = ""; }
          else if (mode === "newtab") { help.textContent = S.download.iosHint; help.hidden = false; }
          else { help.textContent = S.download.blocked; help.hidden = false; }
        }
        track("pl_pdf_download");
        return true;
      }).catch(function (e) {
        try { console.warn("[IB Letter] PDF export failed:", e); } catch (_) {}
        if (help) { help.textContent = S.download.blocked; help.hidden = false; }
        return false;
      });
    }

    return { prepare: prepare, download: download, warmMotif: warmMotif };
  })();

  /* ============================================================== share */
  function shareUrl() {
    var payload = { v: 1, a: state.answers, e: state.edited ? state.composed : null };
    var packed = window.LZString.compressToEncodedURIComponent(JSON.stringify(payload));
    var url = location.origin + location.pathname + "?k=" + packed;
    if (url.length > 1800) track("pl_share_long_url");
    return url;
  }
  function doShare() {
    var url = shareUrl();
    var done = function () { track("pl_share"); showAfter(); };
    if (navigator.share) {
      navigator.share({ title: S.share.title, url: url }).then(done).catch(function () { /* cancelled */ });
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(function () { flashShareNote(S.share.copied); done(); })
        .catch(function () { manualCopy(url); done(); });
    } else {
      manualCopy(url); done();
    }
  }
  function flashShareNote(msg) {
    var help = byId("ib-pl-download-help");
    if (help) { help.textContent = msg; help.hidden = false; }
  }
  function manualCopy(url) {
    var help = byId("ib-pl-download-help");
    if (!help) return;
    help.textContent = "";
    help.appendChild(h("p", null, S.share.copyFail));
    var input = h("input", "ib-pl-input");
    input.type = "text"; input.readOnly = true; input.value = url;
    input.addEventListener("focus", function () { input.select(); });
    help.appendChild(input);
    help.hidden = false;
  }

  /* ====================================================== post (relational) */
  function showAfter() {
    var root = byId("ib-pl-after");
    root.textContent = "";
    var wrap = h("div", "ib-pl-after");
    wrap.appendChild(h("h2", "ib-pl-after__head", S.after.head));
    wrap.appendChild(h("p", "ib-pl-after__body", S.after.body));

    var links = h("div", "ib-pl-after__links");
    links.appendChild(btn("ib-pl-btn ib-pl-btn--secondary", S.after.downloadAgain, function () {
      showView("ib-pl-reveal");
    }));
    links.appendChild(btn("ib-pl-btn ib-pl-btn--link", S.after.again, function () {
      clearSaved();
      state.answers = freshAnswers();
      state.history = []; state.screen = "intro";
      state.composed = ""; state.edited = false;
      var help = byId("ib-pl-download-help");
      if (help) help.hidden = true;
      showView("ib-pl-intro");
    }));
    if (CONFIG.showResourceLink && CONFIG.resourceUrl) {
      var res = h("a", "ib-pl-after__resource", CONFIG.resourceLabel || "For partners: how to support her");
      res.href = CONFIG.resourceUrl;
      links.appendChild(res);
    }
    wrap.appendChild(links);
    root.appendChild(wrap);
    showView("ib-pl-after");
  }

  /* ============================================================== decode */
  function truncate(str, n) { return typeof str === "string" ? str.slice(0, n) : ""; }
  function filterIds(key, arr) {
    var idx = IDX[key];
    return Array.isArray(arr) ? arr.filter(function (id) { return !!idx[id]; }) : [];
  }
  function sanitizeAnswers(raw) {
    if (!raw || typeof raw !== "object" || raw.v !== 1) return null;
    var a = freshAnswers();
    a.from = truncate(raw.from, 60);
    a.to = truncate(raw.to, 60);
    a.phys = filterIds("phys", raw.phys);
    a.emo = filterIds("emo", raw.emo);
    a.trig = filterIds("trig", raw.trig);
    a.not = Array.isArray(raw.not) ? filterIds("not", raw.not) : defaultNot();
    a.help = filterIds("help", raw.help);
    a.load = filterIds("load", raw.load);
    a.core = filterIds("core", raw.core).slice(0, 3);
    a.tone = TONE_IDX[raw.tone] ? raw.tone : "tender";
    a.motif = MOTIF_IDX[raw.motif] ? raw.motif : DEFAULT_MOTIF;
    if (raw.ps && typeof raw.ps === "object") {
      a.ps.t = truncate(raw.ps.t, 600);
      a.ps.none = !!raw.ps.none;
    }
    return a;
  }
  function tryDecodeShareLink() {
    var match = /[?&]k=([^&]+)/.exec(location.search);
    if (!match) return false;
    var parsed = null;
    try { parsed = JSON.parse(window.LZString.decompressFromEncodedURIComponent(match[1])); }
    catch (e) { parsed = null; }
    var answers = parsed && sanitizeAnswers(parsed.a || parsed);
    if (!answers) {
      try { history.replaceState(null, "", location.pathname); } catch (e) { /* noop */ }
      return false;
    }
    state.answers = answers;
    state.fromShare = true;
    state.screen = "reveal";
    if (parsed && typeof parsed.e === "string" && parsed.e.trim()) {
      state.composed = truncate(parsed.e, 8000);
      state.edited = true;
    } else {
      state.composed = composeLetter(answers);
      state.edited = false;
    }
    renderLetterDOM();
    renderMotifRow();
    showView("ib-pl-reveal");
    track("pl_letter_view");
    track("pl_step", "shared_letter");
    pdf.prepare();
    return true;
  }

  /* ---------------------------------------------------------- dev hooks */
  function mockAnswers() {
    var a = freshAnswers();
    a.from = "Sarah"; a.to = "Michael";
    a.phys = ["sleep", "fatigue", "brain_fog", "hot_flashes", "palpitations"];
    a.emo = ["on_edge", "short_fuse", "anxious", "not_myself", "lonely"];
    a.trig = ["chewing", "joke_crit", "fixing", "whats_wrong", "unseen"];
    a.not = ["not_you", "still_love", "not_pushing", "not_fight", "coming_back"];
    a.help = ["sit", "listen", "reassure", "hug_ask", "patience", "stay"];
    a.load = ["meals", "kids_out", "mental_list", "night_off", "decisions"];
    a.core = ["doing_best", "miss_me", "with_you"];
    a.tone = "tender";
    a.motif = "wildflowers";
    a.ps = { t: "I read this back and cried, which honestly proves the whole point. I love you. Thank you for not giving up on me while I figure out who I am on the other side of this.", none: false };
    return a;
  }
  function tryDevHooks() {
    var mock = /[?&]pl_mock=full/.test(location.search);
    var screenMatch = /[?&]pl_screen=([a-z0-9_]+)/.exec(location.search);
    if (!mock && !screenMatch) return false;
    state.answers = mockAnswers();
    if (mock) {
      state.composed = composeLetter(state.answers);
      state.screen = "reveal";
      renderLetterDOM();
      showView("ib-pl-reveal");
      pdf.prepare();
      return true;
    }
    go(screenMatch[1]);
    return true;
  }

  /* ================================================================ boot */
  function wireStaticActions(root) {
    root.addEventListener("click", function (ev) {
      var target = ev.target.closest ? ev.target.closest("[data-pl-action]") : null;
      if (!target) return;
      var action = target.getAttribute("data-pl-action");
      if (action === "start") {
        track("pl_start");
        var saved = load();
        if (saved && saved.screen && saved.screen !== "intro") {
          state.answers = saved.answers;
          state.history = saved.history || [];
          state.composed = saved.composed || "";
          state.edited = !!saved.edited;
          if (saved.screen === "reveal") { reveal(); return; }
          go(saved.screen, { silent: false });
        } else {
          go(buildPath()[0]);
        }
      } else if (action === "download") {
        pdf.download().then(function (ok) { if (ok) showAfter(); });
      } else if (action === "share") {
        doShare();
      } else if (action === "edit-toggle") {
        toggleEdit();
      } else if (action === "edit-answers") {
        track("pl_edit_answers");
        if (editing) toggleEdit();
        state.history = [];
        go(buildPath()[0]);
      } else if (action === "print") {
        if (editing) toggleEdit();
        track("pl_print");
        document.documentElement.classList.add("ib-pl-printing");
        window.setTimeout(function () {
          window.print();
          document.documentElement.classList.remove("ib-pl-printing");
        }, 50);
      }
    });
  }

  var booted = false;
  function init() {
    var root = document.getElementById("ib-pl");
    if (!root || booted) return;
    booted = true;
    wireStaticActions(root);
    if (tryDevHooks()) return;
    if (tryDecodeShareLink()) return;
    var saved = load();
    if (saved && saved.screen && saved.screen !== "intro") {
      var cta = root.querySelector(".ib-pl-intro__cta");
      if (cta) cta.textContent = S.resumeYes;
    }
    showView("ib-pl-intro");
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", function () {
    booted = false;
    init();
  });
})();
